import {Annotation} from '../common/annotation.dto';
import {IQcAnnotation} from '../common/qc-annotation.dto';

export interface Workflow {
  accountId: string;
  completedAt: string;
  credentialSummaries: Array<WorkflowCredentialSummary>;
  initiatedAt: string;
  merchantGuid: string;
  startedAt: string;
  workflowDefinitionKey: string;
  workflowExecutionId: string;
  workflowStatus: WorkflowStatus;
}

export interface WorkflowCredentialSummary {
  id: string;
  category: CredentialCategory;
  decisionSummary: WorkflowDecisionSummary;
  channel: string;
}

export interface WorkflowDecisionSummary {
  capability: CapabilityCategory;
  type: DecisionType;
  detail: WorkflowDecisionDetail;
  data: Array<WorkflowDecisionDetail>;
}

export interface WorkflowDecisionDetail {
  label: string;
  value?: string;
}

export interface WorkflowDetail {
  workflowExecutionId: string;
  workflowStatus: WorkflowStatus;
  workflowDefinitionKey: string;
  workflowName: string;
  accountId: string;
  completedAt: string;
  initiatedAt: string;
  startedAt: string;
  name?: string; // Account name
  wipedOutAt?: string;
  originalDefinitionKey: string;
  redirectedWorkflow: boolean;
  isFraudAvailable: boolean;
  customerInternalReference?: string;
  userReference?: string;
  reportingCriteria?: string;
  tokenExpiresAt?: string;
  photinusBlobPresent?: boolean;

  credentials: Credential[];
}

export interface IPartBinaryResponse {
  // Base64 encoded content
  binary: string;
  mimeType: string;
}

export interface IFaceMatchResult {
  workflowExecutionId: string;
  merchantGuid: string;
  credentialId: string;
  accountId: string;
  scanReference: string;
  imageKey: string;
  classifier: string;
  similarity: number | null;
  isSameAccountId: boolean;
  isSameMerchant: boolean;
}

export interface IFaceMatchPart {
  classifier: string;
  // e.g. /api/v1/platform/parts/{accountId}/{credentialId}/{classifier}
  href: string;
  uploadedAt?: string | null;
  wipedOutAt?: string | null;
  biometricsWipedOutAt?: string | null;
  credentialId?: string;
}

export interface IFaceMatchFinding {
  capabilityId: string;
  // Credentials of the current transaction the face match ran on
  credentials?: string[];
  // Images of the current transaction, keyed by credential type (e.g. SELFIE, ID)
  parts?: Record<string, IFaceMatchPart[]>;
  credentialCategory?: string | null;
  primaryCredentialName?: string | null;
  decision?: {
    type: DecisionType;
    label: string | null;
  } | null;
  lookupStatus: string | null;
  // Sorted by similarity, highest first
  matches: IFaceMatchResult[];
}

export interface IFaceMatchFindingsResponse {
  workflowExecutionId?: string;
  accountId?: string;
  merchantGuid?: string;
  findings: IFaceMatchFinding[];
}

export const CtrLookupType = {
  FACE_LOOKUP: 'FACE_LOOKUP',
  DATA_LOOKUP: 'DATA_LOOKUP',
  PARTIAL_IMAGE_SEARCH: 'PARTIAL_IMAGE_SEARCH'
} as const;
// eslint-disable-next-line no-redeclare
export type CtrLookupType = (typeof CtrLookupType)[keyof typeof CtrLookupType];

export const CtrNetworkScope = {
  NETWORK: 'NETWORK',
  ASSOCIATED_NETWORK: 'ASSOCIATED_NETWORK',
  GLOBAL: 'GLOBAL'
} as const;
// eslint-disable-next-line no-redeclare
export type CtrNetworkScope = (typeof CtrNetworkScope)[keyof typeof CtrNetworkScope];

export const CtrRiskLevel = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const;
// eslint-disable-next-line no-redeclare
export type CtrRiskLevel = (typeof CtrRiskLevel)[keyof typeof CtrRiskLevel];

export interface ICtrStatistic {
  totalHighRiskTransactions: number;
  totalMediumRiskTransactions: number;
  totalLowRiskTransactions: number;
  earliestTransactionTime: string | null;
  latestTransactionTime: string | null;
}

export interface ICtrTransaction {
  workflowExecutionId: string;
  workflowDefinitionKey?: string | null;
  merchantGuid: string;
  merchantName?: string | null;
  customerInternalReference?: string | null;
  riskLevel?: CtrRiskLevel | null;
  riskReason?: {
    type?: DecisionType | null;
    detail?: {
      id?: number | null;
      label?: string | null;
    } | null;
    additionalInformation?: string | null;
  } | null;
  createdAt: string;
  isLegacy?: boolean;
  isIDVLiveness?: boolean;
  isCustomerInternalReferenceSame?: boolean;
  isSameAccountId?: boolean;
  isBiometricWipedout?: boolean;
  isSameUserReference?: boolean;
}

export interface ICtrNetworkResult {
  statistic: ICtrStatistic | null;
  transactions: ICtrTransaction[];
}

export interface ICtrLookupResult {
  lookupType: CtrLookupType;
  lookupStatus: string;
  statistic: ICtrStatistic | null;
  network?: ICtrNetworkResult | null;
  associatedNetwork?: ICtrNetworkResult | null;
  globalNetwork?: ICtrNetworkResult | null;
}

// One LOOKUPS capability of the transaction
export interface ICtrCapability {
  capabilityId: string;
  credentials?: string[];
  audit?: {
    mode?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
  } | null;
  decision?: {
    type?: DecisionType | null;
    detailId?: number | null;
    label?: string | null;
    additionalInformation?: string | null;
    riskScore?: number | null;
  } | null;
  lookupTypes?: string[];
  decisionRulesetSource?: string | null;
  decisionRulesetId?: string | null;
  // Status per lookup type, e.g. {FACE_LOOKUP: 'EXECUTED', PARTIAL_IMAGE_SEARCH: 'FEATURE_NOT_ENABLED'}
  lookupStatus?: Record<string, string> | null;
  // Whether a network's results are shown to the merchant, per lookup type and network scope
  retrievalDisplaySettings?: Record<string, Partial<Record<CtrNetworkScope, boolean>>> | null;
  // Result per lookup type: face match, data match and partial image search
  faceMatchedTransactions?: ICtrLookupResult | null;
  matchedTransactions?: ICtrLookupResult | null;
  matchedImagesTransactions?: ICtrLookupResult | null;
}

export interface ICtrResponse {
  workflowExecutionId: string;
  accountId?: string;
  merchantGuid?: string;
  lookups: ICtrCapability[];
  // Images of the current transaction, keyed by credential type (e.g. SELFIE, ID, FACEMAP)
  parts?: Record<string, IFaceMatchPart[]> | null;
}

export interface PhotinusPreviewResponse {
  image: string;
  contentType: string;
  width: number;
  height: number;
}

export interface IMerchant {
  id: string;
  name: string;
  publicGuid?: string;
}

export interface WorkflowDetailAP extends WorkflowDetail {
  merchantSettings: {[key: string]: string};
  merchant?: IMerchant;
}

export interface MerchantTransaction {
  id: string;
  name: string;
  publicGuid: string;
}

export interface TransactionFeedback {
  reason: string;
  status: string;
  providedAt: string;
}

export interface OriginalTransaction {
  merchantGuid: string;
  accountId: string;
  workflowExecutionId: string;
}

export interface KYXAPTransactionDetail extends WorkflowDetailAP {
  childWorkflowExecutionIds: string[];
  parentWorkflowExecutionId: string;
  silentWorkflow: boolean;
  decision: Decision;
  riskScore: number;
  finalDecision: string;
  stepDecisions: {[key: string]: string};
  // Keyed by CredentialCategory. Unlike credentials[].parts[], these Parts carry qcAnnotations.
  parts: Record<string, Part[]>;
  merchant?: MerchantTransaction;
  feedback: TransactionFeedback;
  preparedData: Record<string, unknown>;
  rulesets: RuleSetObject;
  reusableIdentity?: ReusableIdentity;
  originalTransaction?: OriginalTransaction;
}

export interface Decision {
  riskScore: number;
  type: string;
  label: string;
  id: number;
  options: {[key: string]: string};
}

export interface RuleSetObject {
  [key: string]: RuleSet[];
}

export interface Rule {
  id: string;
  name: string;
  version: number;
  score: number;
  triggeredAt: string | null;
}

export interface RuleSet {
  name: string;
  version: number;
  score: number;
  rulesetId: string;
  startedAt: string;
  completedAt: string;
  capabilityExecutionId: string;
  rulesMatched: Rule[];
  rulesNotMatched: Rule[];
}

export interface ReusableIdentityCredential {
  id: string;
  category: CredentialCategory | null;
  parts: Part[];
  capabilities: CapabilityMapped[];
  decision: {
    type: DecisionType;
    detail: {
      id?: string;
      label: string;
    };
  } | null;
  clientIP: string | null;
}

export interface ReusableIdentity {
  sourceWorkflowExecutionId: string;
  sourceCredentials: ReusableIdentityCredential[];
  sourceMerchantGuid: string;
  sourceTransactionCredentialRegion: string | null;
  idType: string;
  country: string;
  qualifiedIdSource: string;
  qualifiedIdAccepted: boolean;
  localDataSourceRawResponse: string;
}

export interface CapabilityMapped {
  label: CapabilityCategory;
  data: CapabilityData[];
  audits: CapabilityData[];
  options: CapabilityData[];
  primaryCredentialName?: string; // AP Detail Page
  decision: {
    type: DecisionType;
    detail: {
      id: string;
      label: string;
    };
    additionalInformation?: string;
  };
}

export interface CapabilityData {
  label: string;
  value: string;
  type?: WorkflowCapabilityType;
}

export interface Part {
  classifier: PartClassifier;
  uploadedAt?: string;
  wipedOutAt?: string;
  href: string;
  parentKey?: string; // Where is the part originally coming from
  imageAnnotations?: Annotation[];
  // Only populated on the top-level KYXAPTransactionDetail.parts map, not on credentials[].parts[]
  qcAnnotations?: IQcAnnotation[];
  stepId?: string;
  credentialId?: string;
}

export interface Credential {
  id: string;
  category: CredentialCategory;
  parts?: Part[];
  capabilities: CapabilityMapped[];
  clientIP?: string;

  groupedCapabilities?: {[key: string]: CapabilityMapped[]}; // AP Detail Page - grouped Capability by primaryCredentialName

  decision?: {
    type: DecisionType;
    detail: {
      id?: string;
      label: string;
    };
  };
}

export enum CredentialCategory {
  ID = 'ID',
  ID_IV_Verification = 'ID and Identity Verification',
  IV = 'Identity Verification',
  ID_V = 'ID Verification',
  DOC_V = 'Document Verification',
  FACEMAP = 'FACEMAP',
  SELFIE = 'SELFIE',
  DOCUMENT = 'DOCUMENT',
  SCREENING = 'SCREENING',
  ADDRESS_VERIFICATION = 'Address Verification'
}

export enum CapabilityCategory {
  USABILITY = 'USABILITY',
  STORAGE = 'STORAGE',
  EXTRACTION = 'EXTRACTION',
  DATA_CHECKS = 'DATA_CHECKS',
  IMAGE_CHECKS = 'IMAGE_CHECKS',
  LIVENESS = 'LIVENESS',
  SIMILARITY = 'SIMILARITY',
  LOOKUPS = 'LOOKUPS',
  AUTHENTICATION = 'AUTHENTICATION',
  SCREENING = 'WATCHLIST_SCREENING'
}

export enum DecisionType {
  PASSED = 'PASSED',
  WARNING = 'WARNING',
  REJECTED = 'REJECTED',
  NOT_EXECUTED = 'NOT_EXECUTED'
}

export enum PartClassifier {
  FRONT = 'FRONT',
  BACK = 'BACK',
  FACE = 'FACE',
  LIVENESS = 'LIVENESS',
  FACEMAP = 'FACEMAP',
  ORIGIN = 'ORIGIN', // Document original pdf file
  ORIGIN_DDL = 'ORIGIN_DDL' // Document original pdf file (DDL)
}

export enum WorkflowCapabilityType {
  URL = 'URL',
  DATE = 'DATE',
  STRING_LIST = 'STRING_LIST'
}

export type WorkflowStatus = 'INITIATED' | 'TOKEN_EXPIRED' | 'SESSION_EXPIRED' | 'ACQUIRED' | 'PROCESSED' | 'PROCESSING' | 'EXPIRED';

export enum WorkflowStatusName {
  INITIATED = 'INITIATED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  ACQUIRED = 'ACQUIRED',
  PROCESSED = 'PROCESSED',
  PROCESSING = 'PROCESSING'
}
